import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, Mail } from "lucide-react";

export default function Verify() {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  
  const email = location.state?.email || localStorage.getItem("rtrl_verify_email") || "";

  useEffect(() => {
    if (!email) {
      toast.error("No verification email terminal reference found.");
      navigate("/register");
    }
  }, [email, navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/verify", { email, code: code.trim() });
      if (r.data?.token) {
        localStorage.setItem("rtrl_token", r.data.token);
      }
      localStorage.removeItem("rtrl_verify_email");
      await refresh();
      toast.success("Access verified! Terminal activated successfully.");
      navigate("/profile");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0 || resending) return;
    setResending(true);
    try {
      await api.post("/auth/resend-code", { email });
      toast.success("A new verification code has been dispatched to your email terminal.");
      setCountdown(60);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-24">
      <div className="rtrl-card carbon-bg p-8 border border-rtrl-red/30 shadow-[0_0_40px_rgba(229,25,25,0.05)] relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
        <div className="scanline" />

        <div className="relative z-10 text-center space-y-4">
          <ShieldCheck className="w-12 h-12 text-rtrl-red mx-auto animate-pulse" />
          <div>
            <div className="small-label text-rtrl-red">Terminal Security</div>
            <h1 className="font-display uppercase text-4xl text-white mt-1">Verification Required</h1>
            <p className="text-white/60 text-xs mt-2 max-w-sm mx-auto">
              An activation transmission containing a secure verification code has been sent to your terminal at: <br/>
              <span className="text-rtrl-red font-mono-tech mt-1 inline-block break-all">{email}</span>
            </p>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-6 text-left">
            <div>
              <label className="small-label flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Verification Code</label>
              <input
                data-testid="verify-code"
                type="text"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="######"
                required
                className="rtrl-input tracking-[0.5em] text-center font-bold text-2xl placeholder-white/10"
              />
            </div>
            
            <button
              data-testid="verify-submit"
              disabled={busy || code.length < 6}
              className="rtrl-btn w-full justify-center bg-rtrl-red hover:bg-[#ff2222]"
            >
              {busy ? "Activating Uplink…" : "Activate Terminal"}
            </button>
          </form>

          <div className="pt-4 border-t border-surface-line/40 text-center">
            <button
              onClick={handleResend}
              disabled={countdown > 0 || resending}
              className="text-xs font-mono-tech text-rtrl-silver hover:text-white transition-colors"
              data-testid="verify-resend"
            >
              {countdown > 0 ? `Resend Code (${countdown}s)` : "Resend Verification Code"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}